<template>
  <div class="question-container image-responsive">
    <div class="row">
      <div class="col-12 col-md-6 mb-5" v-if="quizOrExamMode">
        <progress-component :min-value="0" :max-value="100" :current-value="questionNumberPercentage" />
      </div>
    </div>
    <div v-if="Object.entries(question).length > 0" class="question">
      <div class="row">
        <div class="col question-wording" v-html="decodeHtml(question.wording)"></div>
      </div>
      <div class="row question-hint-tag-wrapper" v-if="isMultipleChoice && !question.has_text_answer">
        <div class="col">
          <span v-if="question.has_order" class="badge bg-primary question-hint-tag">
            Ziehe die Elemente in die richtige Reihenfolge
          </span>
          <span v-else-if="question.has_matching_answer" class="badge bg-primary question-hint-tag">
            <span class="d-none d-xl-block">Ziehe die Antworten der ersten Liste passend in die Felder der zweiten Liste.</span>
            <span class="d-block d-xl-none">Wähle die passenden Antworten zu den Kategorien durch Anklicken aus.</span>
          </span>
          <span v-else class="badge bg-primary question-hint-tag">Mehrfachauswahl möglich</span>
        </div>
      </div>
      <div class="row">
        <div class="col-12 question-answers" :class="{ 'col-lg-8': evaluationMode }">
          <form @submit.prevent v-if="
            !question.has_order &&
            !question.has_text_answer &&
            !question.has_matching_answer
          ">
            <div class="form-check answer-container" :class="{
              'evaluation-mode': evaluationMode,
              wa:
                evaluationMode &&
                !isCorrectAnswer(answer.id) &&
                isSelectedAnswer(answer.id),
              ra: evaluationMode && isCorrectAnswer(answer.id),
            }" v-for="answer in question.answers" :key="answer.id" v-if="isMultipleChoice">
              <input class="form-check-input" type="checkbox" :id="`${question.id}-${answer.id}`" :value="answer.id"
                v-model="selectedAnswers" />
              <label class="form-check-label" :for="`${question.id}-${answer.id}`" v-html="decodeHtml(answer.wording)">
              </label>
            </div>
            <div class="form-check answer-container" :class="{
              'evaluation-mode': evaluationMode,
              wa:
                evaluationMode &&
                !isCorrectAnswer(answer.id) &&
                isSelectedAnswer(answer.id),
              ra: evaluationMode && isCorrectAnswer(answer.id),
            }" v-for="answer in question.answers" v-else>
              <input class="form-check-input" type="radio" :id="`${question.id}-${answer.id}`" :value="answer.id"
                v-model="selectedAnswers" />
              <label class="form-check-label radio-label" :for="`${question.id}-${answer.id}`"
                v-html="decodeHtml(answer.wording)">
              </label>
            </div>
          </form>
          <template v-else-if="question.has_order">
            <div>
              <div class="row order-answers">
                <div class="col">
                  <draggable class="drag-container selected-list" :class="{
                    'evaluation-mode': evaluationMode,
                    wa: evaluationMode && !evaluationResult,
                    ra: evaluationMode && evaluationResult,
                  }" ghost-class="dragging-ghost" chosen-class="dragging-chosen" :options="{
                    animation: 200,
                    forceFallback: true,
                    fallbackOnBody: true,
                  }" :list="selectedAnswers" itemKey="id">
                    <template #item="{ element }">
                      <div class="answer-container drag-element d-flex align-items-center justify-content-between">
                        <div v-html="decodeHtml(element.wording)" class="flex-grow-1"></div>
                        <span class="badge badge-primary" v-if="evaluationMode">
                          {{ element.order }}</span>
                      </div>
                    </template>
                  </draggable>
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="question.has_matching_answer">
            <div>
              <div class="row matching-answers" v-if="evaluationMode">
                <div class="col-12">
                  <div class="accordion accordion-flush" id="selectedAnswersAccordion">
                    <div class="accordion-item" v-for="category in question.matching_categories"
                      :key="`selection-${question.id}-category-${category.id}`"
                      :id="`selection-${question.id}-category-${category.id}`">
                      <h2 class="accordion-header" :class="{
                        'matching-success-header': correctCategoryMatch(
                          category.id,
                        ),
                      }" :id="`heading-selection-${question.id}-category-${category.id}`">
                        <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                          :data-bs-target="`#selection-${question.id}-category-${category.id}-answers`"
                          aria-expanded="false" aria-controls="flush-collapseOne">
                          {{ category.wording }}
                        </button>
                      </h2>
                      <div :id="`selection-${question.id}-category-${category.id}-answers`"
                        class="accordion-collapse collapse drag-container selected-list"
                        :aria-labelledby="`heading-selection-${question.id}-category-${category.id}`"
                        data-bs-parent="#selectedAnswersAccordion">
                        <div class="answer-container drag-element d-flex align-items-center justify-content-between"
                          v-for="answer in selectedAnswers[category.id]">
                          <div v-html="decodeHtml(answer.wording)" class="flex-grow-1"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <h4 class="mt-3">Richtige Übereinstimmungen</h4>
                  <div class="accordion accordion-flush" id="correctAnswersAccordion">
                    <div class="accordion-item" v-for="category in question.matching_categories"
                      :key="`correct-${question.id}-category-${category.id}`"
                      :id="`correct-${question.id}-category-${category.id}`">
                      <h2 class="accordion-header matching-success-header"
                        :id="`heading-correct-${question.id}-category-${category.id}`">
                        <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                          :data-bs-target="`#correct-${question.id}-category-${category.id}-answers`"
                          aria-expanded="false" aria-controls="flush-collapseOne">
                          {{ category.wording }}
                        </button>
                      </h2>
                      <div :id="`correct-${question.id}-category-${category.id}-answers`"
                        class="accordion-collapse collapse drag-container answers-list"
                        :aria-labelledby="`heading-selection-${question.id}-category-${category.id}`"
                        data-bs-parent="#correctAnswersAccordion">
                        <div class="answer-container drag-element d-flex align-items-center justify-content-between"
                          v-for="answer in category.matched_answers">
                          <div v-html="decodeHtml(answer.wording)" class="flex-grow-1"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row matching-answers d-flex flex-column flex-xl-row gap-3" v-else>
                <!-- Left Column: Available Answers (Desktop) -->
                <div class="d-none d-xl-block" style="width: 50vw; flex-shrink: 0;">
                  <div class="sticky-top" style="top: 1rem;">
                    <div class="answers-list" style="max-height: 70vh; overflow-y: auto;">
                      <draggable :list="question.answers"
                        :group="{ name: 'matching-answers', pull: 'clone', put: false }" item-key="id">
                        <template #item="{ element }">
                          <div class="answer-item" v-html="decodeHtml(element.wording)"></div>
                        </template>
                      </draggable>
                    </div>
                  </div>
                </div>
                <!-- Right Column: Categories -->
                <div class="flex-grow-1">
                  <div class="row g-3">
                    <div v-for="category in question.matching_categories"
                      :key="`${question.id}-answer-category-${category.id}`" class="col-12 col-md-6 col-xxl-4">
                      <div class="card category-card h-100">
                        <div class="card-header bg-light">
                          <h6 class="m-0 text-truncate" :title="category.wording">
                            {{ category.wording }}
                          </h6>
                        </div>
                        <div class="card-body p-2">
                          <!-- Desktop: Draggable Area -->
                          <div class="d-none d-xl-block"
                            style="min-height: 150px; max-height: 250px; overflow-y: auto;">
                            <draggable class="drag-container selected-list" :list="selectedAnswers[category.id]"
                              group="matching-answers" item-key="id"
                              @add="(event) => addAnswerToCategory(event, category.id)">
                              <template #item="{ element }">
                                <div class="selected-answer" v-html="decodeHtml(element.wording)"></div>
                              </template>
                            </draggable>
                          </div>

                          <!-- Mobile: Simple Buttons -->
                          <div class="d-xl-none">
                            <div v-for="answer in question.answers" :key="`cat-${category.id}-ans-${answer.id}`"
                              class="mb-2">
                              <button
                                class="btn btn-sm btn-select category-answer-btn w-100 text-start text-truncate flex-grow-1"
                                :class="{ active: isCategorySelectedAnswer(category.id, answer.id) }"
                                @click.prevent="selectCategoryAnswer(category.id, answer)" style="min-width: 0;">
                                <div v-html="decodeHtml(answer.wording)"></div>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="question.has_text_answer">
            <div>
              <div class="row">
                <div class="col-12 col-md-4 offset-md-3">
                  <input-component name="user_answer" id="user_answer" label="Antwort" placeholder="Antwort" type="text"
                    :value="userAnswer" v-model="userAnswer" :disabled="evaluationMode" aria-label="Deine Antwort" />
                </div>
              </div>
            </div>
          </template>
        </div>
        <Transition name="slide-fade">
          <div v-if="evaluationMode" class="col-12 col-lg-4 question-explanation image-responsive">
            <div class="d-flex flex-column">
              <question-explanation-component :explanation="questionExplanation" :correct="evaluationResult" />
            </div>
          </div>
        </Transition>
      </div>
      <div class="row" v-if="!overviewMode">
        <div class="col">
          <div class="d-grid gap-5 d-flex justify-content-around justify-content-md-end mt-4">
            <button class="btn btn-secondary">Frage: {{ question.id }}</button>
            <template v-if="examMode">
              <button class="btn btn-primary" type="button" @click.prevent="checkAnswer">
                Weiter
              </button>
            </template>
            <template v-else>
              <button class="btn btn-secondary" type="button" v-if="questionNumber > 1"
                @click.prevent="previousQuestion">
                <i class="fa-solid fa-arrow-left"></i>
                Zurück
              </button>
              <button v-if="evaluationMode && quizOrExamMode" class="btn btn-primary" type="button"
                @click.prevent="nextQuestion">
                Fortsetzen
                <i class="fa-solid fa-arrow-right"></i>
              </button>
              <button v-else class="btn btn-primary" type="button" @click.prevent="checkAnswer">
                überprüfen
              </button>
            </template>
          </div>
        </div>
        <div class="row">
          <div class="col text-center">
            <i class="fa-regular fa-comment" v-on:click="showComments"></i>
            <span style="padding: 5px"> </span>
            <span class="bookmark">
              <i @click.prevent="bookmarkQuestion" class="fa-bookmark"
                :class="userQuestion !== null ? 'fa-solid' : 'fa-regular'"></i>
            </span>
          </div>
        </div>
        <div class="row" v-show="commentVisible">
          <div class="col">
            <h1>Frage kommentieren</h1>
            <textarea class="form-control comment-texteditor texteditor" name="comment" id="comment"
              aria-label="Dein Kommentar"></textarea>
            <br />
            <div class="text-right">
              <button class="btn btn-primary" v-bind:class="{ disabled: !canSubmitComment }" type="button"
                @click.prevent="createComment">
                Absenden
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container-fluid" v-else>
      <div class="row">
        <div class="col">
          <div class="d-flex justify-content-center">
            <div class="spinner-grow" style="width: 3rem; height: 3rem" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import draggable from "vuedraggable";
import ProgressComponent from "@client/components/ProgressComponent.vue";
import QuestionExplanationComponent from "@client/components/QuestionExplanationComponent.vue";
import authHeader from "@/auth.header.js";
import { API_URL } from "@/conf.js";

import "katex/dist/katex.css";
import katex from "katex";
window.katex = katex;

import { textArea } from "@/textarea.js";
import { mapState } from "vuex";
import InputComponent from "@client/components/InputComponent.vue";

export default {
  name: "question-component",
  props: {
    question: {
      type: Object,
      default: {},
    },
    questionNumber: {
      type: Number,
      default: 1,
    },
    totalQuestions: {
      type: Number,
      default: 1,
    },
    quizOrExamMode: {
      type: Boolean,
      default: true,
    },
    examMode: {
      type: Boolean,
      default: false,
    },
    quizId: {
      type: Number,
      default: 0,
    },
    overviewMode: {
      type: Boolean,
      default: false,
    },
    overviewSelectedAnswers: {
      type: [Array, Object],
      default: () => [],
    },
    previousQuestionMode: {
      type: Boolean,
      default: false,
    },
    userQuestion: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedAnswers: [],
      evaluationMode: false,
      evaluationResult: false,
      comments: [],
      commentVisible: false,
      comment: "",
      canSubmitComment: false,
      orderQuestionEvaluationModeAnswers: [],
      userAnswer: "",
    };
  },
  mounted() {
    (($) => {
      textArea();

      $("#comment").on("summernote.change", () => {
        this.comment = $("#comment").summernote("code");

        if (this.comment !== "") {
          this.canSubmitComment = true;
        } else {
          this.canSubmitComment = false;
        }
      });
    })(jQuery);
  },
  methods: {
    nextQuestion() {
      this.evaluationMode = false;
      this.evaluationResult = false;
      this.selectedAnswers = [];
      this.userAnswer = "";
      this.$emit("next-question");
    },
    previousQuestion() {
      this.evaluationMode = false;
      this.evaluationResult = false;
      this.selectedAnswers = [];
      this.userAnswer = "";
      this.$emit("previous-question");
    },
    checkAnswer() {
      const headers = authHeader();

      const input = {
        question_id: this.question.id,
        selected_answers: this.prepareSelectedAnswersForCheck(),
        user_answer: this.userAnswer,
      };
      const instance = this;

      const quiz_id =
        parseInt(this.quizId) > 0 ? `/${parseInt(this.quizId)}` : "";

      axios
        .post(`${API_URL}quiz/check${quiz_id}`, input, {
          headers: headers,
        })
        .then((response) => {
          if (response.status === 200) {
            if (!this.examMode) {
              this.evaluationMode = true;
              this.evaluationResult = response.data.result;

              // Order the question answers for correct order display in the case of order questions.
              /* if (this.question.has_order) {
                this.orderQuestionEvaluationModeAnswers = [...this.questions.answers].sort((a, b) => a.order - b.order);
              } */
            }

            this.$emit(
              "track-success-rate",
              this.evaluationResult === true ? "Correct" : "Wrong",
            );
            if (this.examMode) {
              this.nextQuestion();
            }
          } else {
            // alert('Ein technischer Fehler ist aufgetreten, bitte versuche es erneut.');
            this.submitting = false;
            setTimeout(() => {
              instance.error_message = "";
            }, 10000);
          }
        })
        .catch((error) => {
          console.log(error);
          alert(
            "Ein technischer Fehler ist aufgetreten, bitte versuche es erneut.",
          );
          this.submitting = false;
          setTimeout(() => {
            instance.error_message = "";
          }, 10000);
        });
    },
    prepareSelectedAnswersForCheck() {
      let selected_answers = this.selectedAnswers;
      // First consider the order questions since the vuedraggable@next library adds answer object in it's entirity to the selectAnswers array.
      // We need to extract only the answer ids in their given order.
      if (this.question.has_order) {
        // extract only the answer ids.
        selected_answers = this.selectedAnswers.map((obj) => obj.id);
      }

      // For Matching answers, the selectedAnswers is an object with key as category id and value as array of answers matched to category.
      // Issue is that because of clone behavior of vue draggable next, we get the whole answer object instead of only.
      // Rebuild selected objects, and strip the selected answers in each category to only id.
      else if (this.question.has_matching_answer) {
        selected_answers = Object.fromEntries(
          Object.entries(this.selectedAnswers).map(([categoryId, answers]) => [
            categoryId,
            answers.map((answer) => answer.id),
          ]),
        );
      }

      // while creating input, we should consider the behavior of radio buttons using single value instead of declared array.
      // Even for radio buttons,if no value is selected, the modelled data is unchanged.
      else if (!this.isMultipleChoice) {
        // Single choice, uses radio buttons
        selected_answers = Array.isArray(this.selectedAnswers)
          ? this.selectedAnswers
          : [this.selectedAnswers];
      }

      return selected_answers;
    },
    selectCategoryAnswer(category_id, answer) {
      const index = this.selectedAnswers[category_id].findIndex(
        (ans) => ans.id === answer.id,
      );
      if (index >= 0) {
        this.selectedAnswers[category_id].splice(index, 1);
      } else {
        this.selectedAnswers[category_id].push(answer);
      }
    },
    addAnswerToCategory(event, category_id) {
      const newAnswer = this.selectedAnswers[category_id][event.newIndex];
      const isDuplicate = this.selectedAnswers[category_id].some(
        (ans, index) => ans.id === newAnswer.id && index !== event.newIndex,
      );

      // Quitely remove the newly added item to prevent duplicates.
      if (isDuplicate)
        this.selectedAnswers[category_id].splice(event.newIndex, 1);
    },
    isCategorySelectedAnswer(category_id, answer_id) {
      const index = this.selectedAnswers[category_id].findIndex(
        (ans) => ans.id === answer_id,
      );
      return index >= 0;
    },
    isSelectedAnswer(id) {
      return this.isMultipleChoice
        ? this.selectedAnswers.includes(id)
        : this.selectedAnswers == id;
    },
    isCorrectAnswer(id) {
      const question_answers = this.question.questions_answers;

      let answer = question_answers.find((item) => item.answer_id === id);

      return answer.correct === 1;
    },
    loadComments() {
      const headers = authHeader();

      axios
        .get(`${API_URL}`, {
          headers: headers,
        })
        .then((response) => { })
        .catch((error) => {
          this.$swal("Error", "Comment not created", "error");
        });
    },
    createComment() {
      if (this.comment === "") {
        return;
      }

      this.canSubmitComment = false;

      const headers = authHeader();

      axios
        .post(
          `${API_URL}comments/create`,
          {
            user_id: this.user.id,
            question_id: this.question.id,
            comment: this.comment,
          },
          {
            headers: headers,
          },
        )
        .then((response) => {
          if (response.status === 200) {
            $("#comment").summernote("reset");
            this.comment = "";
            this.canSubmitComment = false;
            this.$swal("Success", "Kommentar gespeichert", "success");
          } else {
            this.canSubmitComment = true;
            this.$swal(
              "Info",
              "Kommentar konnte nicht gespeichert werden",
              "info",
            );
          }
        })
        .catch((error) => {
          this.canSubmitComment = true;
          this.$swal(
            "Error",
            "Kommentar konnte nicht gespeichert werden",
            "error",
          );
        });
    },
    showComments() {
      this.commentVisible = !this.commentVisible;
      window.scrollTo(0, document.body.scrollHeight);
    },
    bookmarkQuestion() {
      const headers = authHeader();

      axios
        .post(
          `${API_URL}question/bookmark`,
          {
            user_id: this.user.id,
            question_id: this.question.id,
          },
          {
            headers: headers,
          },
        )
        .then((response) => {
          if (response.status === 200) {
            this.$emit("bookmark-change", true);
          }
        })
        .catch((error) => { });
    },
    correctCategoryMatch(categoryId) {
      let categorySelectedAnswers = this.selectedAnswers[categoryId];
      const category = this.question.matching_categories.find(
        (cat) => cat.id === categoryId,
      );

      if (!category) return false;

      let categoryMatchedAnswerIds = category.matched_answers.map(
        (matched_answer) => matched_answer.id,
      );

      if (categorySelectedAnswers.length !== categoryMatchedAnswerIds.length)
        return false;

      for (let selectedAnswer of categorySelectedAnswers) {
        if (!categoryMatchedAnswerIds.includes(selectedAnswer.id)) return false;
      }

      return true;
    },
  },
  watch: {
    overviewMode: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Set evaluation mode
          this.evaluationMode = true;
          this.evaluationResult =
            parseInt(this.question.pivot.status) == 2 ? true : false;
        }
      },
    },
    overviewSelectedAnswers: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;

        let answersLength = 0;
        if (Array.isArray(newVal)) answersLength = newVal.length;
        else if (typeof newVal === "object" && newVal !== null)
          answersLength = Object.keys(newVal).length;

        // Text answers should be assigned a length of 1
        if (this.question.has_text_answer)
          answersLength = 1;

        if (answersLength > 0 && this.overviewMode) {
          if (this.question.has_order) {
            this.selectedAnswers = newVal.map((selected_answer) => {
              const answer = this.question.answers.find(
                (a) => a.id === selected_answer,
              );
              const questionAnswer = this.question.questions_answers.find(
                (qa) => qa.answer_id === selected_answer,
              );
              return {
                ...answer,
                order: questionAnswer ? questionAnswer.order : null,
              };
            });
          } else if (this.question.has_matching_answer) {
            this.selectedAnswers = [];
            this.question.matching_categories.forEach((category) => {
              const categorySelectedAnswerIds = newVal[category.id];
              let categorySelectedAnswers = [];
              for (let answerId of categorySelectedAnswerIds) {
                const answer = this.question.answers.find(
                  (ans) => ans.id === answerId,
                );
                if (answer)
                  categorySelectedAnswers.push({
                    ...answer,
                  });
              }
              this.selectedAnswers[category.id] = [...categorySelectedAnswers];
            });
          } else if (this.question.has_text_answer) {
            this.userAnswer =
              this.question.quiz_questions_answers !== null
                ? this.question.quiz_questions_answers.text_answer
                : "";
          } else {
            this.selectedAnswers = this.isMultipleChoice
              ? Array.from(newVal)
              : newVal[0];
          }
        }
      },
    },
    question: {
      immediate: true,
      handler(val) {
        if (this.overviewMode) return; // This should be evaluated under the overviewSelectedAnswers watcher.

        if (val !== null && this.previousQuestionMode) {
          this.evaluationMode = true;
          this.evaluationResult =
            parseInt(this.question.pivot.status) == 2 ? true : false;

          if (val.has_order) {
            this.selectedAnswers = val.selected_answers.map(
              (selected_answer) => {
                const answer = val.answers.find(
                  (a) => a.id === selected_answer,
                );
                const questionAnswer = val.questions_answers.find(
                  (qa) => qa.answer_id === selected_answer,
                );
                return {
                  ...answer,
                  order: questionAnswer ? questionAnswer.order : null,
                };
              },
            );
          } else if (val.has_text_answer) {
            this.userAnswer =
              val.quiz_questions_answers !== null
                ? val.quiz_questions_answers.text_answer
                : "";
          } else if (val.has_matching_answer) {
            this.selectedAnswers = [];
            val.matching_categories.forEach((category) => {
              const categorySelectedAnswerIds =
                val.selected_answers[category.id];
              let categorySelectedAnswers = [];
              for (let answerId of categorySelectedAnswerIds) {
                const answer = val.answers.find((ans) => ans.id === answerId);
                if (answer) categorySelectedAnswers.push({ ...answer });
              }
              this.selectedAnswers[category.id] = [...categorySelectedAnswers];
            });
          } else {
            this.selectedAnswers = this.isMultipleChoice
              ? Array.from(this.question.selected_answers)
              : this.question.selected_answers[0];
          }
        } else if (val !== null && val.has_order) {
          // Order question, use availableDndAnswers to prevent mutating question props
          this.selectedAnswers = val.answers.map((answer) => {
            const questionAnswer = val.questions_answers.find(
              (qa) => qa.answer_id === answer.id,
            );
            return {
              ...answer,
              order: questionAnswer ? questionAnswer.order : null,
            };
          });
        } else if (val !== null && val.has_matching_answer) {
          this.selectedAnswers = Object.fromEntries(
            val.matching_categories.map((category) => [category.id, []]),
          );
        }
      },
    },
  },
  computed: {
    ...mapState(["isAuthenticated", "user"]),
    questionNumberPercentage() {
      return (this.questionNumber / this.totalQuestions) * 100;
    },
    isMultipleChoice() {
      const answers = this.question.questions_answers;
      let expectedCount = answers.filter(
        (answer) => answer.correct === true || answer.correct === 1,
      ).length;
      return expectedCount > 1 ? true : false;
    },
    questionExplanation() {
      if (!this.question.has_order) return this.question.explanation;

      return `<p><b>Die Zahlen in den Elementen geben dir die richtige Reihenfolge an.</b></p><br>${this.question.explanation}`;
    },
  },
  components: {
    InputComponent,
    ProgressComponent,
    QuestionExplanationComponent,
    draggable,
  },
};
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.matching-success-header {
  background: #4e9152 0% 0% no-repeat padding-box;

  .accordion-button {
    background: #4e9152 0% 0% no-repeat padding-box;
  }
}
</style>
